import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { UsAddressInput } from '@/lib/us-address-validate';
import { validateSenderForm } from '@/lib/us-address-validate';
import styles from './MailCardSenderForm.module.css';

interface MailCardSenderFormValue {
  name: string;
  address: UsAddressInput;
}

interface MailCardSenderFormProps {
  value: MailCardSenderFormValue;
  onChange: (next: MailCardSenderFormValue) => void;
  onSubmit: () => void;
}

/**
 * Phase 2C Q4 surface — sender name + US return address.
 *
 * Known-sender is the only posture (the name-hidden sender path
 * that would have skipped this surface was removed 2026-05-29).
 *
 * Visual + validation register mirrors `MailCardRecipientForm`
 * exactly; the two are intentionally parallel (plan § 4.3) so a
 * third caller can later motivate a shared `AddressFields`
 * extraction (CLAUDE.md rule #4 — extract on the second
 * occurrence of duplication; this IS the second, but the plan
 * specs separate components for v1 — extraction is banked).
 */
export default function MailCardSenderForm({
  value,
  onChange,
  onSubmit,
}: MailCardSenderFormProps) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const result = validateSenderForm(value.name, value.address);
  const shouldShow = (field: string) =>
    submitted || touched.has(field);

  const errorFor = (field: keyof typeof result.errors): string | null => {
    if (!shouldShow(field)) return null;
    const key = result.errors[field];
    if (!key) return null;
    return t(`mailCard.${key}`);
  };

  const touch = (field: string) => {
    setTouched((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  const updateName = (next: string) => {
    onChange({ ...value, name: next });
  };

  const updateAddress = (patch: Partial<UsAddressInput>) => {
    onChange({ ...value, address: { ...value.address, ...patch } });
  };

  const handleRegionBlur = () => {
    const trimmed = value.address.region.trim();
    if (trimmed.length === 2 && trimmed !== trimmed.toUpperCase()) {
      updateAddress({ region: trimmed.toUpperCase() });
    }
    touch('region');
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    if (result.ok) onSubmit();
  };

  const nameErr = errorFor('name');
  const line1Err = errorFor('line1');
  const line2Err = errorFor('line2');
  const cityErr = errorFor('city');
  const regionErr = errorFor('region');
  const postalErr = errorFor('postalCode');

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.heading}>{t('mailCard.senderPrompt')}</h2>

      <label className={styles.field}>
        <span className={styles.label}>
          {t('mailCard.senderNameLabel')}
        </span>
        <input
          type="text"
          className={`${styles.input} ${nameErr ? styles.inputError : ''}`}
          value={value.name}
          onChange={(e) => updateName(e.target.value)}
          onBlur={() => touch('name')}
          autoCapitalize="words"
          autoComplete="name"
          aria-invalid={Boolean(nameErr)}
        />
        {nameErr && <span className={styles.error} role="alert">{nameErr}</span>}
      </label>

      <label className={styles.field}>
        <span className={styles.label}>
          {t('mailCard.senderLine1Label')}
        </span>
        <input
          type="text"
          className={`${styles.input} ${line1Err ? styles.inputError : ''}`}
          value={value.address.line1}
          onChange={(e) => updateAddress({ line1: e.target.value })}
          onBlur={() => touch('line1')}
          autoCapitalize="words"
          autoComplete="address-line1"
          aria-invalid={Boolean(line1Err)}
        />
        {line1Err && <span className={styles.error} role="alert">{line1Err}</span>}
      </label>

      <label className={styles.field}>
        <span className={styles.label}>
          {t('mailCard.senderLine2Label')}
        </span>
        <input
          type="text"
          className={`${styles.input} ${line2Err ? styles.inputError : ''}`}
          value={value.address.line2}
          onChange={(e) => updateAddress({ line2: e.target.value })}
          onBlur={() => touch('line2')}
          autoCapitalize="words"
          autoComplete="address-line2"
          aria-invalid={Boolean(line2Err)}
        />
        {line2Err && <span className={styles.error} role="alert">{line2Err}</span>}
      </label>

      <label className={styles.field}>
        <span className={styles.label}>
          {t('mailCard.senderCityLabel')}
        </span>
        <input
          type="text"
          className={`${styles.input} ${cityErr ? styles.inputError : ''}`}
          value={value.address.city}
          onChange={(e) => updateAddress({ city: e.target.value })}
          onBlur={() => touch('city')}
          autoCapitalize="words"
          autoComplete="address-level2"
          aria-invalid={Boolean(cityErr)}
        />
        {cityErr && <span className={styles.error} role="alert">{cityErr}</span>}
      </label>

      <div className={styles.row}>
        <label className={`${styles.field} ${styles.regionField}`}>
          <span className={styles.label}>
            {t('mailCard.senderRegionLabel')}
          </span>
          <input
            type="text"
            className={`${styles.input} ${regionErr ? styles.inputError : ''}`}
            value={value.address.region}
            onChange={(e) => updateAddress({ region: e.target.value })}
            onBlur={handleRegionBlur}
            autoCapitalize="characters"
            autoComplete="address-level1"
            maxLength={2}
            aria-invalid={Boolean(regionErr)}
          />
          {regionErr && <span className={styles.error} role="alert">{regionErr}</span>}
        </label>

        <label className={`${styles.field} ${styles.postalField}`}>
          <span className={styles.label}>
            {t('mailCard.senderPostalLabel')}
          </span>
          <input
            type="text"
            className={`${styles.input} ${postalErr ? styles.inputError : ''}`}
            value={value.address.postalCode}
            onChange={(e) => updateAddress({ postalCode: e.target.value })}
            onBlur={() => touch('postalCode')}
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            aria-invalid={Boolean(postalErr)}
          />
          {postalErr && <span className={styles.error} role="alert">{postalErr}</span>}
        </label>
      </div>

      <button type="submit" className={styles.continueBtn}>
        {t('mailCard.senderContinueBtn')}
      </button>
    </form>
  );
}
